const mergeByProp = (prop, a = [], b = [], rulesFn, opts = {}) => {
  const aArr = a || [];
  const bArr = b || [];
  const aLen = aArr.length;
  const bLen = bArr.length;

  if (aLen === 0) {
    return bArr;
  }
  if (bLen === 0) {
    return aArr;
  }

  const propList = Array.isArray(prop) ? prop : [prop];
  const isSingleProp = propList.length === 1;
  const singleProp = isSingleProp ? propList[0] : null;

  const keyFor = isSingleProp
    ? (item = {}) => {
        const val = item?.[singleProp];
        return val === undefined || val === null ? '__undefined__' : String(val);
      }
    : (item = {}) => propList.map((p) => item?.[p] ?? '__undefined__').join('::');

  if (opts?.keepPosition) {
    const incomingByKey = new Map();
    for (let i = 0; i < bLen; i++) {
      const item = bArr[i];
      incomingByKey.set(keyFor(item), item);
    }

    const seenKeys = new Set();
    const merged = new Array(aLen);

    for (let i = 0; i < aLen; i++) {
      const current = aArr[i];
      const key = keyFor(current);
      seenKeys.add(key);
      const incoming = incomingByKey.get(key);
      if (incoming) {
        merged[i] = typeof rulesFn === 'function' ? rulesFn(current, incoming) : incoming;
      } else {
        merged[i] = current;
      }
    }

    const appended = [];
    for (let i = 0; i < bLen; i++) {
      const item = bArr[i];
      const key = keyFor(item);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        appended.push(item);
      }
    }

    if (appended.length === 0) {
      return merged;
    }
    return merged.concat(appended);
  }

  // Build maps with cached keys
  const bByKey = new Map();
  for (let i = 0; i < bLen; i++) {
    const item = bArr[i];
    bByKey.set(keyFor(item), item);
  }

  const aKeys = new Set();
  for (let i = 0; i < aLen; i++) {
    aKeys.add(keyFor(aArr[i]));
  }

  if (typeof rulesFn !== 'function') {
    const uniqueA = [];
    for (let i = 0; i < aLen; i++) {
      const item = aArr[i];
      if (!bByKey.has(keyFor(item))) {
        uniqueA.push(item);
      }
    }
    if (uniqueA.length === 0) {
      return bArr;
    }
    return uniqueA.concat(bArr);
  }

  const uniqueA = [];
  const uniqueB = [];
  const merged = [];
  let uniqueACount = 0;
  let uniqueBCount = 0;
  let mergedCount = 0;

  for (let i = 0; i < aLen; i++) {
    const item = aArr[i];
    const key = keyFor(item);
    const bItem = bByKey.get(key);
    if (bItem) {
      merged[mergedCount++] = rulesFn(item, bItem);
    } else {
      uniqueA[uniqueACount++] = item;
    }
  }

  for (let i = 0; i < bLen; i++) {
    const item = bArr[i];
    if (!aKeys.has(keyFor(item))) {
      uniqueB[uniqueBCount++] = item;
    }
  }

  // Pre-allocate result array for better performance
  const totalLen = uniqueACount + uniqueBCount + mergedCount;
  const result = new Array(totalLen);
  let resultIdx = 0;

  for (let i = 0; i < uniqueACount; i++) {
    result[resultIdx++] = uniqueA[i];
  }
  for (let i = 0; i < uniqueBCount; i++) {
    result[resultIdx++] = uniqueB[i];
  }
  for (let i = 0; i < mergedCount; i++) {
    result[resultIdx++] = merged[i];
  }

  return result;
};

export default mergeByProp;
